import React, { useEffect, useRef, useState } from 'react';

/**
 * Global long-press tooltip for touch devices.
 *
 * Mounts once at the App root. Listens for touchstart on any element that has
 * a `title` or `aria-label` attribute. After a long press (~450ms), shows a
 * floating tooltip with the label text. This gives mobile users discoverability
 * for icon-only buttons without requiring per-component changes.
 *
 * Desktop behavior is unchanged — native `title` tooltips still appear on hover.
 */
const LONG_PRESS_MS = 450;
const HIDE_MS = 2200;

export function TouchTooltip() {
  const [tooltip, setTooltip] = useState(null); // { text, x, y }
  const pressTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const stashedTitleRef = useRef(null); // { el, value } so we can restore it
  const isTouchDeviceRef = useRef(false);

  useEffect(() => {
    // Only enable on touch-capable devices. Desktops keep native hover tooltips.
    isTouchDeviceRef.current =
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));

    if (!isTouchDeviceRef.current) return;

    const findLabelTarget = (target) => {
      // Walk up the DOM looking for the nearest element with a label
      let el = target;
      while (el && el !== document.body) {
        // Skip elements that have visible text already (no need for tooltip)
        if (el.tagName === 'BUTTON' || el.tagName === 'A' || el.getAttribute('role') === 'button') {
          const title = el.getAttribute('title');
          const ariaLabel = el.getAttribute('aria-label');
          const label = title || ariaLabel;
          if (label && label.trim()) {
            // Heuristic: only show tooltip if the button has no meaningful visible text
            // (icon-only). If it already has text content longer than the label, skip.
            const text = (el.innerText || '').trim();
            if (!text || text.length < 3 || text === label) {
              return { el, label };
            }
          }
        }
        el = el.parentElement;
      }
      return null;
    };

    const clearTimers = () => {
      if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
      if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    };

    const restoreTitle = () => {
      if (stashedTitleRef.current) {
        const { el, value } = stashedTitleRef.current;
        if (el && value !== null) el.setAttribute('title', value);
        stashedTitleRef.current = null;
      }
    };

    const handleTouchStart = (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      const match = findLabelTarget(e.target);
      if (!match) return;

      const { el, label } = match;
      const x = touch.clientX;
      const y = touch.clientY;

      pressTimerRef.current = setTimeout(() => {
        // Temporarily strip `title` to suppress iOS Safari's native callout
        const currentTitle = el.getAttribute('title');
        if (currentTitle !== null) {
          stashedTitleRef.current = { el, value: currentTitle };
          el.removeAttribute('title');
        }
        setTooltip({ text: label, x, y });
        hideTimerRef.current = setTimeout(() => {
          setTooltip(null);
          restoreTitle();
        }, HIDE_MS);
      }, LONG_PRESS_MS);
    };

    const handleTouchEnd = () => {
      // If user lifted before long-press fired, cancel
      if (pressTimerRef.current && !hideTimerRef.current) {
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
      }
    };

    const handleTouchMove = () => {
      // If the finger moves significantly, cancel the upcoming tooltip
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
      }
    };

    const handleScrollOrTap = () => {
      if (tooltip) {
        setTooltip(null);
        restoreTitle();
      }
      clearTimers();
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    window.addEventListener('scroll', handleScrollOrTap, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchcancel', handleTouchEnd);
      window.removeEventListener('scroll', handleScrollOrTap);
      clearTimers();
      restoreTitle();
    };
  }, [tooltip]);

  if (!tooltip) return null;

  // Clamp position so the tooltip stays on screen
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  const left = Math.max(12, Math.min(vw - 12 - 200, tooltip.x - 100));
  const top = Math.max(40, tooltip.y - 56);

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left, top, maxWidth: 240 }}
      role="tooltip"
      data-testid="touch-tooltip"
    >
      <div className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap overflow-hidden text-ellipsis">
        {tooltip.text}
      </div>
      <div
        className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1"
        aria-hidden="true"
      />
    </div>
  );
}

export default TouchTooltip;
